import React, { useEffect, useRef, useState, useCallback, useContext } from 'react';
import { Bold, Italic, Link, Sparkles, Loader2 } from 'lucide-react';
import { InlineEditContext } from './InlineEditContext';
import axios from 'axios';

const getApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.startsWith('/')) {
    return `${window.location.origin}${envUrl}`;
  }
  return envUrl || `${window.location.protocol}//${window.location.hostname}:8001/api`;
};

interface ToolbarPosition {
  top: number;
  left: number;
}

/**
 * FloatingToolbar — appears above selected text inside any InlineEditText element.
 *
 * Mount this once near the root of the SiteBuilder canvas. It listens to
 * document selectionchange events and positions itself above the selection
 * bounding rect whenever text is selected inside a .inline-edit-active element.
 */
const FloatingToolbar: React.FC = () => {
  const ctx = useContext(InlineEditContext);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<ToolbarPosition>({ top: 0, left: 0 });
  const [rewriting, setRewriting] = useState(false);

  // Saved range so we can restore selection before execCommand
  const savedRangeRef = useRef<Range | null>(null);

  const TOOLBAR_HEIGHT = 40; // px — approximate; used for offset
  const TOOLBAR_OFFSET = 8;  // gap between toolbar bottom and selection top

  const computePosition = useCallback((rect: DOMRect): ToolbarPosition => {
    const toolbarWidth = toolbarRef.current?.offsetWidth ?? 220;

    let left = rect.left + rect.width / 2 - toolbarWidth / 2;
    // Clamp horizontally inside viewport
    left = Math.max(8, Math.min(left, window.innerWidth - toolbarWidth - 8));

    const top = rect.top - TOOLBAR_HEIGHT - TOOLBAR_OFFSET + window.scrollY;
    return { top: Math.max(8, top), left };
  }, []);

  const hideToolbar = useCallback(() => {
    setVisible(false);
    savedRangeRef.current = null;
  }, []);

  // Listen for selection changes
  useEffect(() => {
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        // Delay slightly so clicks on toolbar buttons don't immediately hide it
        setTimeout(() => {
          const sel2 = window.getSelection();
          if (!sel2 || sel2.isCollapsed) setVisible(false);
        }, 100);
        return;
      }

      const range = sel.getRangeAt(0);
      const container = range.commonAncestorContainer;

      // Walk up to check if the selection is inside an inline-edit-active element
      const node = container.nodeType === Node.TEXT_NODE
        ? container.parentElement
        : (container as Element);

      const editableEl = node?.closest('.inline-edit-active');
      if (!editableEl) {
        hideToolbar();
        return;
      }

      savedRangeRef.current = range.cloneRange();

      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        hideToolbar();
        return;
      }

      setPosition(computePosition(rect));
      setVisible(true);
    };

    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [computePosition, hideToolbar]);

  // Hide when clicking outside the toolbar
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        const target = e.target as Element;
        if (!target.closest?.('.inline-edit-active')) {
          hideToolbar();
        }
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [hideToolbar]);

  // Restore saved selection range (selection may be lost after toolbar click)
  const restoreSelection = () => {
    if (!savedRangeRef.current) return;
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(savedRangeRef.current);
  };

  const handleBold = (e: React.MouseEvent) => {
    e.preventDefault();
    restoreSelection();
    document.execCommand('bold');
    // Persist the change: find the focused editable element and trigger its blur/change
    triggerContentUpdate();
  };

  const handleItalic = (e: React.MouseEvent) => {
    e.preventDefault();
    restoreSelection();
    document.execCommand('italic');
    triggerContentUpdate();
  };

  const handleLink = (e: React.MouseEvent) => {
    e.preventDefault();
    restoreSelection();
    const url = window.prompt('Enter URL:', 'https://');
    if (url && url.trim()) {
      document.execCommand('createLink', false, url.trim());
      triggerContentUpdate();
    }
  };

  const handleRewrite = async (e: React.MouseEvent) => {
    e.preventDefault();

    const sel = window.getSelection();
    const selectedText = savedRangeRef.current
      ? savedRangeRef.current.toString()
      : sel?.toString() ?? '';

    if (!selectedText.trim()) return;

    // Build context string from InlineEditContext if available
    const componentId = ctx?.componentId ?? 'website section';
    const controls = ctx?.controls ?? {};
    const contextParts: string[] = [`component: ${componentId}`];
    if (controls.headline) contextParts.push(`headline: "${controls.headline}"`);
    if (controls.subheadline) contextParts.push(`subheadline: "${controls.subheadline}"`);
    const contextStr = contextParts.join(', ');

    setRewriting(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.post(
        `${getApiBase()}/site-builder/rewrite-text`,
        { text: selectedText, context: contextStr, tone: 'professional' },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const rewritten: string = res.data.rewritten_text ?? '';
      if (!rewritten) return;

      // Restore selection and replace text
      restoreSelection();
      const sel2 = window.getSelection();
      if (sel2 && sel2.rangeCount > 0) {
        const range = sel2.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(rewritten));
        // Collapse after inserted text
        range.collapse(false);
        sel2.removeAllRanges();
        sel2.addRange(range);
      }

      triggerContentUpdate();
      hideToolbar();
    } catch (err) {
      console.error('AI rewrite failed', err);
    } finally {
      setRewriting(false);
    }
  };

  /**
   * After an execCommand mutation, find the contentEditable ancestor and
   * synthetically trigger a blur → calls InlineEditText's handleBlur which
   * calls onControlChange to persist the update.
   */
  const triggerContentUpdate = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const container = sel.getRangeAt(0).commonAncestorContainer;
    const node = container.nodeType === Node.TEXT_NODE
      ? container.parentElement
      : (container as Element);
    const editableEl = node?.closest('[contenteditable="true"]') as HTMLElement | null;
    if (editableEl) {
      // Fire an input event so React-managed state stays in sync
      editableEl.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  if (!visible) return null;

  return (
    <div
      ref={toolbarRef}
      className="sb-floating-toolbar"
      style={{ top: position.top, left: position.left }}
      // Prevent mousedown from stealing focus / collapsing the selection
      onMouseDown={e => e.preventDefault()}
    >
      <button onClick={handleBold} title="Bold" aria-label="Bold">
        <Bold size={14} />
      </button>

      <button onClick={handleItalic} title="Italic" aria-label="Italic">
        <Italic size={14} />
      </button>

      <button onClick={handleLink} title="Insert link" aria-label="Insert link">
        <Link size={14} />
      </button>

      <div className="divider" aria-hidden="true" />

      <button
        onClick={handleRewrite}
        title="Rewrite with AI"
        aria-label="Rewrite with AI"
        disabled={rewriting}
        style={{ gap: 4 }}
      >
        {rewriting
          ? <Loader2 size={14} className="sb-toolbar-spin" />
          : <Sparkles size={14} />
        }
        <span style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
          {rewriting ? 'Rewriting…' : 'Rewrite'}
        </span>
      </button>
    </div>
  );
};

export default FloatingToolbar;
